/**
 * Périodes de formation → état des cellules du calendrier — source UNIQUE (lot 5.6).
 *
 * POURQUOI CE FICHIER EXISTE.
 * Le tableau `cellStates` (« quel type de journée sur telle date, dans telle
 * colonne ? ») est le cœur du module : c'est lui que le calendrier peint, que le
 * tableau de bord compte et que l'export PDF imprime. Il n'est pourtant écrit
 * nulle part en base sous cette forme — il se DÉDUIT des périodes, qui sont des
 * intervalles (`date_debut`, `date_fin`, `type`) rattachés à un sous-niveau.
 *
 * Cette déduction vivait dans `Calendar.js`, et l'écran d'export en avait besoin
 * à son tour. Le recopier aurait créé deux calculs pour une seule notion : le
 * jour où le rattachement d'une colonne à un groupe changerait, l'un des deux
 * suivrait et l'autre non — le calendrier et son PDF montreraient alors deux
 * plannings différents, ce qui est précisément le défaut qu'un export ne doit
 * jamais avoir.
 *
 * ⚠️ LA CLÉ EST `AAAA-MM-JJ-<index de colonne>`, et l'index est la POSITION de la
 * colonne dans la liste à plat de toutes les colonnes de tous les niveaux. Ce
 * n'est pas un identifiant : insérer un niveau au Paramétrage décale les index.
 * C'est le format historique, partagé par le calendrier, le tableau de bord et
 * les exports — le changer demanderait de les changer tous le même jour.
 */

/**
 * Toutes les colonnes du planning, à plat et dans l'ordre d'affichage.
 *
 * `index` est la clé de `cellStates` ; `cle` (« BUT1-FI ») sert aux écrans qui
 * doivent nommer une colonne dans un tableau de réglages — un index nu n'y
 * survivrait pas à l'ajout d'un niveau.
 */
export function construireColonnes(config) {
    const colonnes = [];
    (config?.niveaux || []).forEach(niveau => {
        (niveau.colonnes || []).forEach(col => {
            colonnes.push({
                index: colonnes.length,
                cle: niveau.code + '-' + col.code,
                niveauId: niveau.id,
                niveauCode: niveau.code,
                code: col.code,
                label: col.label,
                couleur: col.couleur || '#007BFF'
            });
        });
    });
    return colonnes;
}

/** La clé d'une cellule — une date (chaîne ou Date) et l'index de sa colonne. */
export function cleCellule(date, colIndex) {
    const dateStr = typeof date === 'string'
        ? date.split('T')[0]
        : new Date(date).toISOString().split('T')[0];
    return dateStr + '-' + colIndex;
}

/**
 * La colonne qui affiche un sous-niveau donné, ou −1.
 *
 * Deux rattachements, et le second n'est pas une commodité : une colonne dont le
 * code est « FI » représente le groupe UNIQUE de son niveau quand il n'y en a
 * qu'un — c'est la configuration de la plupart des départements, où « BUT 1 »
 * n'a pas de sous-groupe nommé « FI » en base.
 */
function indexColonneDuSousNiveau(colonnes, sousNiveau, sousNiveaux, deptId, niveauId) {
    return colonnes.findIndex(c => {
        if (c.niveauId !== niveauId) return false;
        if (c.code.toUpperCase() === sousNiveau.code.toUpperCase()) return true;
        if (c.code.toUpperCase() === 'FI') {
            const duNiveau = sousNiveaux.filter(s => s.niveau_id === c.niveauId && s.departement_id === deptId);
            return duNiveau.length === 1 && duNiveau[0].id === sousNiveau.id;
        }
        return false;
    });
}

/**
 * Le sous-niveau (groupe) qu'une colonne affiche, ou `null`.
 *
 * C'est la lecture INVERSE de la précédente, et elle sert aux écrans qui
 * s'adressent à la base par groupe alors que l'utilisateur, lui, raisonne par
 * colonne — les préférences d'export sont rangées par sous-niveau depuis
 * toujours.
 */
export function sousNiveauDeColonne(colonne, config) {
    const sousNiveaux = config?.sousNiveaux || [];
    const deptId = config?.departement?.id;
    const duNiveau = sousNiveaux.filter(s => s.niveau_id === colonne.niveauId && s.departement_id === deptId);
    const exact = duNiveau.find(s => s.code.toUpperCase() === colonne.code.toUpperCase());
    if (exact) return exact;
    if (colonne.code.toUpperCase() === 'FI' && duNiveau.length === 1) return duNiveau[0];
    return null;
}

/**
 * Développe les périodes en état par cellule.
 *
 * Une période qui ne retombe sur aucune colonne affichée est IGNORÉE, sans
 * bruit : c'est le cas normal d'un groupe masqué au Paramétrage, pas une erreur.
 */
export function construireCellStates(periodes, config) {
    const colonnes = construireColonnes(config);
    const sousNiveaux = config?.sousNiveaux || [];
    const deptId = config?.departement?.id;
    const etats = {};

    (periodes || []).forEach(periode => {
        const sousNiveau = sousNiveaux.find(s =>
            s.id === periode.sous_niveau_id && s.departement_id === deptId
        );
        if (!sousNiveau) return;

        const colIndex = indexColonneDuSousNiveau(
            colonnes, sousNiveau, sousNiveaux, deptId, periode.niveau_id
        );
        if (colIndex === -1) return;

        const debut = new Date(periode.date_debut);
        const fin = new Date(periode.date_fin);
        for (let d = new Date(debut); d <= fin; d.setDate(d.getDate() + 1)) {
            etats[d.toISOString().split('T')[0] + '-' + colIndex] = periode.type;
        }
    });

    return etats;
}

/**
 * Les jours du calendrier regroupés par mois, dans l'ordre reçu du serveur.
 * Le planning couvre 14 mois (août → septembre suivant) : le regroupement est
 * fait sur (nom du mois, année), et non sur le seul nom, sans quoi les deux
 * septembre du calendrier n'en feraient qu'un.
 */
export function grouperParMois(calendrier) {
    const groupes = {};
    const ordre = [];
    (calendrier || []).forEach(jour => {
        const cle = jour.nom_mois + '-' + jour.annee;
        if (!groupes[cle]) {
            groupes[cle] = { nom: jour.nom_mois, annee: jour.annee, jours: [] };
            ordre.push(cle);
        }
        groupes[cle].jours.push(jour);
    });
    return ordre.map(cle => groupes[cle]);
}
